// A purpose-written demo tune (public domain), generated as a register stream played by a tiny
// 6502 routine. It deliberately uses typical SID techniques – hard restart, delayed vibrato, a
// slide, PWM, a filter sweep, arpeggio chords and noise/sweep drums – so every heuristic has
// something to work on.

import { buildPsid } from '../core/sidfile';
import { assemble } from './asm';

const PAL = 985248;
const SPEED = 6; // ticks per row → 125 BPM at 50 Hz
const ROWS_PER_BAR = 16;
const BARS = 8;

const freqReg = (midi: number) =>
  Math.min(0xffff, Math.round((440 * Math.pow(2, (midi - 69) / 12) * 16777216) / PAL));

const NOTE: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
function n(name: string): number {
  const m = /^([A-G])(#?)(-?\d)$/.exec(name)!;
  return 12 * (parseInt(m[3], 10) + 1) + NOTE[m[1]] + (m[2] ? 1 : 0);
}

interface VoiceFrame {
  freq: number;
  pw: number;
  ctrl: number;
  ad: number;
  sr: number;
}

const TOTAL = BARS * ROWS_PER_BAR * SPEED;

function emptyVoice(): VoiceFrame[] {
  return Array.from({ length: TOTAL }, () => ({ freq: 0, pw: 0x800, ctrl: 0x40, ad: 0, sr: 0 }));
}

/** Lead: pulse with PWM, hard restart and delayed vibrato; one slide. */
function lead(): VoiceFrame[] {
  const out = emptyVoice();
  // [note, rows]
  const melody: [string | null, number][] = [
    ['A4', 4],
    ['C5', 4],
    ['E5', 4],
    ['D5', 2],
    ['C5', 2],
    ['B4', 6],
    ['G4', 2],
    ['A4', 8],
    ['A4', 4],
    ['C5', 4],
    ['E5', 4],
    ['G5', 2],
    ['F5', 2],
    ['E5', 12],
    [null, 4],
    ['F5', 4],
    ['E5', 4],
    ['D5', 4],
    ['C5', 4],
    ['B4', 4],
    ['C5', 4],
    ['D5', 8],
    ['E5', 4],
    ['D5', 4],
    ['C5', 4],
    ['B4', 4],
    ['A4', 8],
    ['E5', 8],
  ];
  let f = 0;
  let pwPhase = 0;
  for (let i = 0; i < melody.length; i++) {
    const [name, rows] = melody[i];
    const len = rows * SPEED;
    const next = melody[i + 1]?.[0];
    for (let k = 0; k < len && f < TOTAL; k++, f++) {
      const v = out[f];
      pwPhase++;
      v.pw = 0x400 + Math.round(0x300 * (1 + Math.sin(pwPhase / 20)));
      if (!name) continue;
      let pitch = n(name);
      // Delayed vibrato after 12 ticks on long notes (±0.4 semitones).
      if (k > 12 && len > 24) pitch += 0.4 * Math.sin((k - 12) * 0.9);
      // Final note: slide up a fifth during the second half.
      if (i === melody.length - 1 && k > len / 2) pitch += Math.min(7, ((k - len / 2) / (len / 2)) * 14);
      v.freq = freqReg(pitch);
      v.ad = 0x09;
      v.sr = 0xa8;
      // Hard restart: gate off and ADSR cleared 2 ticks before the next note.
      const hr = k >= len - 2 && next;
      if (hr) {
        v.ctrl = 0x40;
        v.ad = 0;
        v.sr = 0;
      } else v.ctrl = 0x41;
    }
  }
  return out;
}

/** Bass: sawtooth eighth notes through a sweeping low-pass filter. */
function bass(): VoiceFrame[] {
  const out = emptyVoice();
  const roots = ['A2', 'A2', 'F2', 'G2', 'A2', 'F2', 'G2', 'E2'];
  for (let bar = 0; bar < BARS; bar++) {
    const root = n(roots[bar]);
    for (let eighth = 0; eighth < 8; eighth++) {
      const note = eighth % 4 === 3 ? root + 12 : root;
      const start = (bar * ROWS_PER_BAR + eighth * 2) * SPEED;
      for (let k = 0; k < 2 * SPEED; k++) {
        const v = out[start + k];
        v.freq = freqReg(note);
        v.ad = 0x05;
        v.sr = 0x60;
        v.ctrl = k < 2 * SPEED - 3 ? 0x21 : 0x20;
      }
    }
  }
  return out;
}

/** Voice 3: drums in bars 1–4, arpeggio chords in bars 5–8. */
function drumsAndArps(): VoiceFrame[] {
  const out = emptyVoice();
  const hit = (start: number, kind: 'kick' | 'snare' | 'hat') => {
    const frames =
      kind === 'kick'
        ? [
            { ctrl: 0x81, midi: 72 },
            ...Array.from({ length: 5 }, (_, i) => ({ ctrl: 0x11, midi: 60 - i * 6 })),
          ]
        : kind === 'snare'
          ? [
              { ctrl: 0x81, midi: 80 },
              { ctrl: 0x41, midi: 55 },
              ...Array.from({ length: 5 }, () => ({ ctrl: 0x81, midi: 78 })),
            ]
          : [
              { ctrl: 0x81, midi: 104 },
              { ctrl: 0x81, midi: 104 },
            ];
    frames.forEach((fr, i) => {
      const v = out[start + i];
      v.freq = freqReg(fr.midi);
      v.ctrl = fr.ctrl;
      v.ad = 0x00;
      v.sr = kind === 'hat' ? 0x00 : 0xa0;
    });
    const end = out[start + frames.length];
    if (end) end.ctrl = frames[frames.length - 1].ctrl & 0xfe;
  };
  for (let bar = 0; bar < 4; bar++) {
    for (let row = 0; row < ROWS_PER_BAR; row += 2) {
      const start = (bar * ROWS_PER_BAR + row) * SPEED;
      if (row === 0 || row === 8) hit(start, 'kick');
      else if (row === 4 || row === 12) hit(start, 'snare');
      else hit(start, 'hat');
    }
  }
  const chords = [
    ['A3', 0, 3, 7],
    ['F3', 0, 4, 7],
    ['C4', 0, 4, 7],
    ['G3', 0, 4, 7],
  ] as const;
  for (let c = 0; c < 4; c++) {
    const [root, ...iv] = chords[c];
    const start = (4 + c) * ROWS_PER_BAR * SPEED;
    const len = ROWS_PER_BAR * SPEED;
    for (let k = 0; k < len; k++) {
      const v = out[start + k];
      v.freq = freqReg(n(root) + iv[k % 3]);
      v.pw = 0x600;
      v.ad = 0x00;
      v.sr = 0x88;
      v.ctrl = k < len - 2 ? 0x41 : 0x40;
    }
  }
  return out;
}

/** Encodes per-frame register changes as (reg, value) pairs, $FF = end of frame, $FE = loop. */
function encodeStream(voices: VoiceFrame[][]): number[] {
  const regs = new Array<number>(25).fill(-1);
  const stream: number[] = [];
  for (let f = 0; f < TOTAL; f++) {
    const want: [number, number][] = [];
    voices.forEach((frames, vi) => {
      const v = frames[f];
      const b = vi * 7;
      want.push([b, v.freq & 0xff], [b + 1, v.freq >> 8], [b + 2, v.pw & 0xff], [b + 3, (v.pw >> 8) & 0x0f]);
      want.push([b + 5, v.ad], [b + 6, v.sr]);
    });
    // Filter: low-pass sweep on voice 2 (the bass).
    const cutoff = Math.round(300 + 900 * (0.5 + 0.5 * Math.sin((f / TOTAL) * Math.PI * 4)));
    want.push([0x15, cutoff & 7], [0x16, cutoff >> 3], [0x17, 0x82], [0x18, 0x1f]);
    // Control registers last, so the gate sees the new frequency and envelope.
    voices.forEach((frames, vi) => want.push([vi * 7 + 4, frames[f].ctrl]));
    for (const [r, v] of want) {
      if (regs[r] !== v) {
        stream.push(r, v);
        regs[r] = v;
      }
    }
    stream.push(0xff);
  }
  stream.push(0xfe);
  return stream;
}

const PLAYER = `
* = $1000
        jmp init
        jmp play
init:   lda #<stream
        sta $fb
        lda #>stream
        sta $fc
        lda #$0f
        sta $d418
        rts
play:   ldy #0
next:   lda ($fb),y
        cmp #$ff
        beq done
        cmp #$fe
        beq restart
        tax
        iny
        lda ($fb),y
        sta $d400,x
        iny
        bne next
done:   iny
        tya
        clc
        adc $fb
        sta $fb
        bcc out
        inc $fc
out:    rts
restart: lda #<stream
        sta $fb
        lda #>stream
        sta $fc
        jmp play
stream:
`;

export function buildDemoTune(): Uint8Array {
  const stream = encodeStream([lead(), bass(), drumsAndArps()]);
  const code = assemble(PLAYER + '        .byte 0\n', 0x1000);
  const streamAddr = code.labels.get('stream')!;
  const data = new Uint8Array(streamAddr - 0x1000 + stream.length);
  data.set(code.bytes.subarray(0, streamAddr - 0x1000));
  data.set(stream, streamAddr - 0x1000);
  return buildPsid({
    name: 'SID2MIDI Demo',
    author: 'SID2MIDI Web contributors',
    released: '2026 Public Domain',
    loadAddress: 0x1000,
    initAddress: 0x1000,
    playAddress: 0x1003,
    data,
  });
}
